import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import type { CmmdCampaign, CmmdProject } from "@cmmd-forge/contracts";
import { cn } from "~/lib/utils";
import {
  ROW_HEIGHT_PX,
  TimelineGrid,
  formatRange,
  parseDate,
  type TimelineLane,
  type TimelineMonth,
  type TimelineScale,
} from "./ProjectsTimelineGrid";
import { projectDisplayName } from "./projectDisplayName";
import { useCmmdRowContextMenu } from "./useCmmdRowContextMenu";

function laneStatus(projects: ReadonlyArray<CmmdProject>): {
  readonly label: string;
  readonly className: string;
} {
  const statuses = projects.map((project) => project.status?.toLowerCase() ?? "");
  if (statuses.some((status) => status.includes("done") || status.includes("complete"))) {
    return {
      label: "Done",
      className: "border-success-border bg-success-surface text-success-foreground",
    };
  }
  if (statuses.some((status) => status.includes("active") || status.includes("progress"))) {
    return {
      label: "Active",
      className: "border-info-border bg-info-surface text-info-foreground",
    };
  }
  return {
    label: "Planned",
    className: "border-border bg-muted text-muted-foreground",
  };
}

export interface TimelineWorkColumnProps {
  readonly datedLanes: ReadonlyArray<TimelineLane>;
  readonly undatedLanes: ReadonlyArray<TimelineLane>;
  readonly projectCount: number;
  readonly width: number;
  readonly onWidthChange: (px: number) => void;
  readonly onResetWidth: () => void;
  readonly onEditCampaign: (campaign: CmmdCampaign) => void;
  /** Triggers campaign delete — caller shows confirm dialog. */
  readonly onRequestDeleteCampaign?: ((campaign: CmmdCampaign) => void) | undefined;
}

export function TimelineWorkColumn({
  datedLanes,
  undatedLanes,
  projectCount,
  width,
  onWidthChange,
  onResetWidth,
  onEditCampaign,
  onRequestDeleteCampaign,
}: TimelineWorkColumnProps) {
  return (
    <div
      // z-30 keeps the sticky Work column above the Today marker (z-20) so the
      // today line/pill scrolls UNDER it instead of overlaying the lane titles.
      className="sticky left-0 z-30 shrink-0 border-r border-border bg-background"
      style={{ width }}
    >
      <div className="sticky top-0 z-20 flex h-11 items-center border-b border-border bg-background/95 px-4 backdrop-blur">
        <span className="text-xs font-semibold text-foreground">Work</span>
        <span className="ml-auto text-[11px] text-muted-foreground">{projectCount} projects</span>
      </div>

      <div>
        {datedLanes.map((lane) => (
          <TimelineWorkColumnRow
            key={`${lane.kind}:${lane.id ?? "none"}:label`}
            lane={lane}
            onEditCampaign={onEditCampaign}
            onRequestDeleteCampaign={onRequestDeleteCampaign}
          />
        ))}

        {undatedLanes.length > 0 ? (
          <div
            className="flex items-center border-b border-border bg-muted px-4 text-[11px] font-medium uppercase text-muted-foreground"
            style={{ height: ROW_HEIGHT_PX }}
          >
            No campaign dates
          </div>
        ) : null}

        {undatedLanes.map((lane) => (
          <TimelineUndatedWorkColumnRow
            key={`${lane.kind}:${lane.id ?? "none"}:undated-label`}
            lane={lane}
            onEditCampaign={onEditCampaign}
            onRequestDeleteCampaign={onRequestDeleteCampaign}
          />
        ))}
      </div>
      <WorkColumnResizeHandle width={width} onWidthChange={onWidthChange} onReset={onResetWidth} />
    </div>
  );
}

// Drag handle straddling the Work column's right border. Drag to resize the
// column (the timeline grid auto-widens as it narrows); double-click to reset.
function WorkColumnResizeHandle({
  width,
  onWidthChange,
  onReset,
}: {
  readonly width: number;
  readonly onWidthChange: (px: number) => void;
  readonly onReset: () => void;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startWidth: width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    onWidthChange(drag.startWidth + (event.clientX - drag.startX));
  };
  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize Work column"
      title="Drag to resize · double-click to reset"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      className="group absolute inset-y-0 right-0 z-30 w-2 translate-x-1/2 cursor-col-resize touch-none"
    >
      <div className="mx-auto h-full w-px bg-transparent transition-colors group-hover:bg-primary/60 group-active:bg-primary" />
    </div>
  );
}

interface TimelineWorkColumnRowProps {
  readonly lane: TimelineLane;
  readonly onEditCampaign: (campaign: CmmdCampaign) => void;
  readonly onRequestDeleteCampaign?: ((campaign: CmmdCampaign) => void) | undefined;
}

function TimelineWorkColumnRow({
  lane,
  onEditCampaign,
  onRequestDeleteCampaign,
}: TimelineWorkColumnRowProps) {
  const rawStart = parseDate(lane.campaign?.startDate);
  const rawEnd = parseDate(lane.campaign?.endDate);
  const status = laneStatus(lane.projects);
  const campaign = lane.campaign;

  const { onContextMenu } = useCmmdRowContextMenu({
    onView: campaign ? () => onEditCampaign(campaign) : undefined,
    onDelete:
      campaign && onRequestDeleteCampaign ? () => onRequestDeleteCampaign(campaign) : undefined,
    labels: { view: "Edit campaign" },
  });

  return (
    <div
      className="group flex items-center gap-3 border-b border-border px-4"
      style={{ height: ROW_HEIGHT_PX }}
      onContextMenu={campaign ? onContextMenu : undefined}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-foreground">{lane.title}</div>
        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
          <span>{formatRange(rawStart, rawEnd)}</span>
          <span>{lane.projects.length} projects</span>
        </div>
      </div>
      <span
        className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${status.className}`}
      >
        {status.label}
      </span>
    </div>
  );
}

interface TimelineUndatedWorkColumnRowProps {
  readonly lane: TimelineLane;
  readonly onEditCampaign: (campaign: CmmdCampaign) => void;
  readonly onRequestDeleteCampaign?: ((campaign: CmmdCampaign) => void) | undefined;
}

function TimelineUndatedWorkColumnRow({
  lane,
  onEditCampaign,
  onRequestDeleteCampaign,
}: TimelineUndatedWorkColumnRowProps) {
  const campaign = lane.campaign;

  const { onContextMenu } = useCmmdRowContextMenu({
    onView: campaign ? () => onEditCampaign(campaign) : undefined,
    onDelete:
      campaign && onRequestDeleteCampaign ? () => onRequestDeleteCampaign(campaign) : undefined,
    labels: { view: "Edit campaign" },
  });

  return (
    <div
      className="group flex items-center gap-3 border-b border-border px-4"
      style={{ height: ROW_HEIGHT_PX }}
      onContextMenu={campaign ? onContextMenu : undefined}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-foreground">{lane.title}</div>
        <div className="mt-0.5 text-[10px] text-muted-foreground">
          {lane.projects.length} projects need dates
        </div>
      </div>
    </div>
  );
}

export interface TimelineChartProps {
  readonly datedLanes: ReadonlyArray<TimelineLane>;
  readonly undatedLanes: ReadonlyArray<TimelineLane>;
  readonly months: ReadonlyArray<TimelineMonth>;
  readonly axisStart: Date;
  readonly axisEnd: Date;
  readonly rowCount: number;
  readonly toPercent: (date: Date) => number;
  readonly onOpenProject: (projectId: string) => void;
}

export function TimelineChart({
  datedLanes,
  undatedLanes,
  months,
  axisStart,
  axisEnd,
  rowCount,
  toPercent,
  onOpenProject,
}: TimelineChartProps) {
  return (
    <div className="relative min-w-0 flex-1">
      <TimelineGrid
        months={months}
        axisStart={axisStart}
        axisEnd={axisEnd}
        rowCount={rowCount}
        toPercent={toPercent}
      />

      <div className="relative pt-11">
        {datedLanes.map((lane) => (
          <TimelineBarRow
            key={`${lane.kind}:${lane.id ?? "none"}:bar`}
            lane={lane}
            axisStart={axisStart}
            axisEnd={axisEnd}
            toPercent={toPercent}
            onOpenProject={onOpenProject}
          />
        ))}

        {undatedLanes.length > 0 ? (
          <div
            className="relative flex items-center border-b border-border bg-muted px-4 text-[11px] text-muted-foreground"
            style={{ height: ROW_HEIGHT_PX }}
          >
            Add campaign dates to place this work on the timeline.
          </div>
        ) : null}

        {undatedLanes.map((lane) => (
          <TimelineUndatedRow
            key={`${lane.kind}:${lane.id ?? "none"}:undated`}
            lane={lane}
            onOpenProject={onOpenProject}
          />
        ))}
      </div>
    </div>
  );
}

interface TimelineBarRowProps {
  readonly lane: TimelineLane;
  readonly axisStart: Date;
  readonly axisEnd: Date;
  readonly toPercent: (date: Date) => number;
  readonly onOpenProject: (projectId: string) => void;
}

function TimelineBarRow({
  lane,
  axisStart,
  axisEnd,
  toPercent,
  onOpenProject,
}: TimelineBarRowProps) {
  const rawStart = parseDate(lane.campaign?.startDate);
  const rawEnd = parseDate(lane.campaign?.endDate);
  const barStart = rawStart ?? axisStart;
  const barEnd = rawEnd ?? axisEnd;
  const leftPct = toPercent(barStart);
  const rightPct = toPercent(barEnd);
  const widthPct = Math.max(rightPct - leftPct, 1.2);

  return (
    <div className="relative flex items-center px-0" style={{ height: ROW_HEIGHT_PX }}>
      <div
        className="absolute h-5 rounded-sm border border-hue-violet-border bg-hue-violet/80 shadow-[0_0_0_1px_rgba(255,255,255,0.05)_inset] transition-colors hover:bg-hue-violet"
        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        title={`${lane.title}: ${formatRange(rawStart, rawEnd)}`}
      />
      <div
        className="absolute flex max-w-[240px] items-center gap-1 overflow-hidden"
        style={{ left: `${Math.min(leftPct + widthPct + 1, 78)}%` }}
      >
        {lane.projects.slice(0, 2).map((project) => (
          <button
            key={project.id}
            type="button"
            onClick={() => onOpenProject(project.id)}
            className="truncate rounded border border-border bg-background/85 px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:border-border hover:text-foreground"
            title={projectDisplayName(project) ?? project.title}
          >
            {projectDisplayName(project) ?? project.title}
          </button>
        ))}
        {lane.projects.length > 2 ? (
          <span className="text-[10px] text-muted-foreground">+{lane.projects.length - 2}</span>
        ) : null}
      </div>
    </div>
  );
}

function TimelineUndatedRow({
  lane,
  onOpenProject,
}: {
  readonly lane: TimelineLane;
  readonly onOpenProject: (projectId: string) => void;
}) {
  return (
    <div
      className="relative flex items-center gap-1 border-b border-border px-4"
      style={{ height: ROW_HEIGHT_PX }}
    >
      {lane.projects.length > 0 ? (
        lane.projects.slice(0, 4).map((project) => (
          <button
            key={project.id}
            type="button"
            onClick={() => onOpenProject(project.id)}
            className="max-w-48 truncate rounded border border-border bg-muted px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title={projectDisplayName(project) ?? project.title}
          >
            {projectDisplayName(project) ?? project.title}
          </button>
        ))
      ) : (
        <span className="text-[10px] text-muted-foreground">No projects</span>
      )}
      {lane.projects.length > 4 ? (
        <span className="ml-1 text-[10px] text-muted-foreground">+{lane.projects.length - 4}</span>
      ) : null}
    </div>
  );
}

const TIMELINE_SCALE_OPTIONS: ReadonlyArray<{ value: TimelineScale; label: string }> = [
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
  { value: "quarters", label: "Quarters" },
];

// Jira-style floating control pinned to the timeline's bottom-right: a Today
// jump plus a segmented Weeks / Months / Quarters zoom toggle.
export function TimelineScaleControl({
  scale,
  onScaleChange,
  onJumpToToday,
}: {
  readonly scale: TimelineScale;
  readonly onScaleChange: (scale: TimelineScale) => void;
  readonly onJumpToToday: () => void;
}) {
  return (
    <div className="absolute right-4 bottom-4 z-30 flex items-center gap-1 rounded-lg border border-border bg-background/95 p-1 shadow-lg backdrop-blur">
      <button
        type="button"
        onClick={onJumpToToday}
        className="rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        Today
      </button>
      <div className="mx-0.5 h-4 w-px bg-border/60" />
      <div className="flex items-center gap-0.5">
        {TIMELINE_SCALE_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => onScaleChange(value)}
            aria-pressed={scale === value}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              scale === value
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
